import {useEffect, useState} from 'react';

import {useKeenSlider} from 'keen-slider/react';

import type {KeenSliderInstance} from 'keen-slider';

/**
 * Custom hook for the `ImageSlider` component.
 * Uses keen-slider for reliable touch/swipe support with optional auto-play.
 *
 * @param slideCount    Total number of slides.
 * @param autoPlayMs    Auto-play interval in ms. 0 disables.
 * @param initialSlide  Initial slide index to display. Defaults to `0`.
 * @param onSlideChange Callback fired when the active slide changes.
 * @returns An object containing:
 * - `activeIndex`: The currently visible slide index.
 * - `sliderRef`: Ref callback to attach to the slider container element.
 */
export const useImageSlider = (
    slideCount: number,
    autoPlayMs: number,
    initialSlide = 0,
    onSlideChange?: (index: number) => void
) => {
    const [activeIndex, setActiveIndex] = useState(initialSlide);

    const [sliderRef, sliderInstanceRef] = useKeenSlider<HTMLDivElement>(
        {
            initial: initialSlide,
            loop: slideCount > 1,
            slides: {perView: 1},
            /**
             * Updates active index when the slide changes.
             *
             * @param slider The keen-slider instance.
             */
            slideChanged(slider) {
                const idx = slider.track.details.rel;

                setActiveIndex(idx);
                onSlideChange?.(idx);
            }
        },
        autoPlayMs > 0
            ? [
                (slider: KeenSliderInstance) => {
                    let timeout: ReturnType<typeof setTimeout>;

                    /** Clears any pending auto-play timer. */
                    const clearNextTimeout = () => {
                        clearTimeout(timeout);
                    };

                    /** Schedules the next auto-advance. */
                    const nextTimeout = () => {
                        clearTimeout(timeout);
                        timeout = setTimeout(() => {
                            slider.next();
                        }, autoPlayMs);
                    };

                    slider.on('created', nextTimeout);
                    slider.on('dragStarted', clearNextTimeout);
                    slider.on('animationEnded', nextTimeout);
                    slider.on('updated', nextTimeout);

                    slider.on('destroyed', clearNextTimeout);
                }
            ]
            : []
    );

    // Client-side page navigations can reuse this component's DOM node (same layout key),
    // so keen-slider's cached slide count goes stale when `images` changes without a remount.
    useEffect(() => {
        sliderInstanceRef.current?.update();
    }, [slideCount, sliderInstanceRef]);

    return {
        activeIndex,
        sliderRef
    };
};